import { Box, Pagination, Stack, Typography } from "@mui/material";
import type { PartListItem, PaginatedParts } from "../services/PartService";
import PartCard from "./PartCard";

export interface PartCardListProps {
  data: PaginatedParts | undefined;
  isLoading: boolean;
  page: number;
  onPageChange: (page: number) => void;
  onPartClick: (part: PartListItem) => void;
  /** Shown when there are no items; defaults to the create-your-first-part hint. */
  emptyMessage?: string;
}

const PartCardList = ({
  data,
  isLoading,
  page,
  onPageChange,
  onPartClick,
  emptyMessage = "No parts found. Create a part or upload an Excel file to get started.",
}: PartCardListProps) => {
  if (isLoading) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography color="text.secondary">Loading parts...</Typography>
      </Box>
    );
  }

  if (!data || data.items.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography color="text.secondary">{emptyMessage}</Typography>
      </Box>
    );
  }

  const totalPages = Math.ceil(data.total / data.page_size);

  return (
    <Stack spacing={2}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: 2,
        }}
      >
        {data.items.map((part) => (
          <PartCard key={part.id} part={part} onClick={onPartClick} />
        ))}
      </Box>

      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_e, value) => onPageChange(value)}
            color="primary"
          />
        </Box>
      )}
    </Stack>
  );
};

export default PartCardList;
